<i18n>
  {
      "no": {
        "kyc": {
          "header": "Identifisering",
          "text": "For å fortsette bestillingen må du bekrefte din identitet. Velg et av alternativene:"
        },
        "login": {
          "header": "Velkommen",
          "text": "Velg din påloggingsleverandør"
        },
        "cancel": "Tilbake"
      },
      "en": {
        "kyc": {
          "header": "Identification",
          "text": "To continue you'll need to verify your identity. Please pick a verification method:"
        },
        "login": {
          "header": "Welcome",
          "text": "Select your login provider"
        },
        "cancel": "Go back"
    }
  }
</i18n>

<template>
  <div class="page">
    <FormWrapper>
      <div>
        <TnHeading type="h1" size="m" align="center" class="margin-vertical-l">
          {{ isKyc ? t('kyc.header') : t('login.header') }}
        </TnHeading>
        <TnHeading type="h2" size="2xs" align="center" class="margin-bottom-l">
          {{ isKyc ? t('kyc.text') : t('login.text') }}
        </TnHeading>

        <Idp v-for="idp in idps" :key="idp.displayName" :idp="idp" :url="getIdpUrl(idp)" />

        <div v-if="requestUrlEncoded" class="margin-top-m text-center">
          <TnLink
            :href="`/account/cancel?requestUrlEncoded=${requestUrlEncoded}`"
            :text="t('cancel')"
          />
        </div>
      </div>
    </FormWrapper>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { TnHeading, TnLink } from '@telenornorgeinternal/telegrafen-vue3-core';

import FormWrapper from '@/components/FormWrapper.vue';
import IdpComponent from '@/components/Idp.vue';
import { IdpService } from './idp.service';
import { RequestParsingService } from '@/views/ibis/requestParsing.service';
import type { Idp as IdpModel, RequestParameters } from '@/types';

// Aliased so the local <Idp> tag does not clash with the Idp type import.
const Idp = IdpComponent;

const { t } = useI18n();
const route = useRoute();

const idps = ref<IdpModel[]>([]);
const request = ref<Partial<RequestParameters> & { idps?: string[] }>({});
const loginContext = ref('');

const requestUrlEncoded = computed(() => route.query.requestUrlEncoded as string | undefined);
const isKyc = computed(() => loginContext.value === 'kyc');

function getIdpUrl(idp: IdpModel) {
  const idpRequest: Record<string, unknown> = { ...request.value };
  idpRequest.authScheme = idp.authScheme;
  idpRequest.idps = null;

  if (!idpRequest.authorizeParams) {
    idpRequest.authorizeParams = {};
  }

  if (idp.amr) {
    (idpRequest.authorizeParams as Record<string, unknown>).amr = idp.amr;
  }

  return `/External/Challenge?requestUrlEncoded=${RequestParsingService.toUrlEncodedJson(idpRequest)}`;
}

onMounted(() => {
  request.value = RequestParsingService.parseUrlEncodedJson(requestUrlEncoded.value) ?? {};
  loginContext.value = request.value.loginContext === 'kyc' ? 'kyc' : 'default';
  idps.value = new IdpService().getIdps(request.value.idps ?? null);
});
</script>
